import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Loader2, RotateCcw, Trash2 } from "lucide-react";

import {
  deleteRegistration,
  exportRegistrationsUrl,
  listRegistrations,
  resetRegistrations,
} from "@/lib/api";
import { AdminPage, Loading } from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/inscrieri")({
  component: RegistrationsPage,
});

type RegistrationRow = {
  id: string;
  full_name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  job_title?: string | null;
  attending?: string | null;
  message?: string | null;
  created_at: string;
};

function RegistrationsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "registrations"],
    queryFn: async () => (await listRegistrations()) as unknown as RegistrationRow[],
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter((row) =>
      [row.full_name, row.email, row.company, row.job_title]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [data, search]);

  async function remove(id: string) {
    try {
      await deleteRegistration(id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Eroare");
      return;
    }
    toast.success("Înscriere ștearsă");
    await queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
  }

  async function resetAll() {
    setResetting(true);
    try {
      await resetRegistrations();
    } catch (e) {
      setResetting(false);
      toast.error(e instanceof Error ? e.message : "Eroare");
      return;
    }
    setResetting(false);
    toast.success("Toate înscrierile au fost șterse");
    setResetOpen(false);
    setResetConfirm("");
    await queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
  }

  function exportCsv() {
    window.open(exportRegistrationsUrl(), "_blank");
  }

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Înscrieri"
      description={`${filtered.length} din ${data?.length ?? 0} înscrieri`}
      action={
        <div className="flex flex-wrap gap-2">
          <Button onClick={exportCsv} variant="outline">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button
            variant="destructive"
            disabled={(data?.length ?? 0) === 0}
            onClick={() => {
              setResetConfirm("");
              setResetOpen(true);
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Resetează înscrierile
          </Button>
        </div>
      }
    >
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Caută după nume, email, companie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Nume</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Companie</th>
              <th className="px-4 py-3">Participă la</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-t border-border align-top">
                <td className="px-4 py-3 font-medium">{row.full_name}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  <div>{row.email}</div>
                  {row.phone && <div>{row.phone}</div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  <div>{row.company}</div>
                  {row.job_title && <div className="text-xs">{row.job_title}</div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{row.attending}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {new Date(row.created_at).toLocaleDateString("ro-RO")}
                </td>
                <td className="px-4 py-3">
                  <Button size="icon" variant="ghost" onClick={() => remove(row.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  Nicio înscriere găsită.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <DialogTitle className="text-center">Resetezi toate înscrierile?</DialogTitle>
            <DialogDescription className="text-center">
              Se vor șterge definitiv <strong>{data?.length ?? 0}</strong> înscrieri. Acțiunea nu
              poate fi anulată — recomandăm un Export CSV înainte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-confirm">
              Scrie <span className="font-mono font-semibold">RESET</span> pentru confirmare
            </Label>
            <Input
              id="reset-confirm"
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="RESET"
              autoComplete="off"
            />
          </div>
          <DialogFooter className="gap-2 sm:justify-center">
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>
              Anulează
            </Button>
            <Button
              variant="destructive"
              disabled={resetConfirm.trim().toUpperCase() !== "RESET" || resetting}
              onClick={resetAll}
            >
              {resetting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Șterge tot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
